import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, downloadFile } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusBadge, StatCard, money, moneyShort } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Receipt, Plus, Loader2, Wallet, AlertTriangle, CheckCircle2, CreditCard, Mail, FileDown, History, CheckCircle, XCircle } from "lucide-react";

export default function Invoices() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  const [payFor, setPayFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [f, setF] = useState({ customer_name: "", subtotal: 100000 });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: invoices = [], isLoading } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });
  const { data: emailLogs = [], isFetching: logsLoading } = useQuery({
    queryKey: ["invoice-emails", historyFor?.id],
    queryFn: async () => (await api.get(`/invoices/${historyFor.id}/emails`)).data,
    enabled: !!historyFor,
  });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/invoices", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); setOpen(false); toast.success("Invoice created"); },
  });
  const payMut = useMutation({
    mutationFn: (invoiceId) => api.post(`/payments/checkout/invoice/${invoiceId}`, { origin_url: window.location.origin }),
    onSuccess: (res) => { window.location.href = res.data.checkout_url; },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not start payment"),
  });
  const emailMut = useMutation({
    mutationFn: (invoiceId) => api.post(`/invoices/${invoiceId}/send-email`),
    onSuccess: (res, invoiceId) => { toast.success(`Invoice emailed to ${res.data.to}`); qc.invalidateQueries({ queryKey: ["invoice-emails", invoiceId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not send email"),
  });

  const submit = () => {
    const cgst = Math.round(f.subtotal * 0.09), sgst = Math.round(f.subtotal * 0.09);
    createMut.mutate({
      customer_name: f.customer_name, gstin: "29ABCDE1234F1Z5",
      items: [{ name: "Implementation & License", qty: 1, price: f.subtotal, hsn: "998314" }],
      subtotal: f.subtotal, cgst, sgst, igst: 0, discount: 0, total: f.subtotal + cgst + sgst,
      amount_paid: 0, status: "unpaid", due_date: new Date(Date.now() + 15 * 864e5).toISOString(),
    });
  };

  const outstanding = invoices.reduce((s, i) => s + (i.status !== "paid" ? (i.total - (i.amount_paid || 0)) : 0), 0);
  const collected = invoices.reduce((s, i) => s + (i.amount_paid || 0), 0);
  const overdue = invoices.filter((i) => i.status === "overdue").length;

  return (
    <div>
      <PageHeader title="Billing · Invoices" subtitle="GST-ready invoicing with online payments" testid="invoices-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-invoice-btn"><Plus className="mr-1.5 h-4 w-4" /> New Invoice</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create invoice</DialogTitle>
            <DialogDescription>Enter the customer and pre-tax amount; GST and totals are computed automatically.</DialogDescription>
          </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Customer</Label><Input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="invoice-customer-input" /></div>
              <div className="space-y-1.5"><Label>Amount (₹, pre-tax)</Label><Input type="number" value={f.subtotal} onChange={(e) => set("subtotal", Number(e.target.value))} data-testid="invoice-amount-input" /></div>
              <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">CGST + SGST (18%)</span><span>{money(Math.round(f.subtotal * 0.18))}</span></div>
                <div className="mt-1 flex justify-between font-semibold"><span>Total</span><span>{money(Math.round(f.subtotal * 1.18))}</span></div>
              </div>
              <DialogFooter><Button onClick={submit} disabled={createMut.isPending || !f.customer_name} data-testid="invoice-save-btn">{createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create invoice"}</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon={Wallet} label="Outstanding" value={moneyShort(outstanding)} tone="warning" index={0} testid="inv-outstanding" />
        <StatCard icon={CheckCircle2} label="Collected" value={moneyShort(collected)} tone="success" index={1} testid="inv-collected" />
        <StatCard icon={AlertTriangle} label="Overdue" value={overdue} tone="critical" index={2} testid="inv-overdue" />
      </div>

      {isLoading ? <Loading /> : invoices.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" description="Create your first GST invoice and share a payment link."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" /> New Invoice</Button>} testid="invoices-empty" />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Invoice #</TableHead><TableHead>Customer</TableHead><TableHead>Total</TableHead>
              <TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell className="font-medium">{money(inv.total)}</TableCell>
                  <TableCell>
                    <div className="w-24">
                      <Progress value={Math.min(100, ((inv.amount_paid || 0) / inv.total) * 100)} className="h-1.5" />
                      <span className="text-[11px] text-muted-foreground">{money(inv.amount_paid || 0)}</span>
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge value={inv.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF"
                        onClick={() => downloadFile(`/invoices/${inv.id}/pdf`, `invoice-${inv.invoice_number}.pdf`).catch(() => toast.error("Could not download PDF"))}
                        data-testid={`pdf-invoice-${inv.id}`}><FileDown className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Email history"
                        onClick={() => setHistoryFor(inv)} data-testid={`history-invoice-${inv.id}`}><History className="h-4 w-4" /></Button>
                      <Button variant="outline" size="sm" onClick={() => emailMut.mutate(inv.id)} disabled={emailMut.isPending && emailMut.variables === inv.id} data-testid={`email-invoice-${inv.id}`}>
                        {emailMut.isPending && emailMut.variables === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="mr-1 h-3.5 w-3.5" /> Email</>}
                      </Button>
                      {inv.status !== "paid" ? (
                        <Button size="sm" onClick={() => setPayFor(inv)} data-testid={`pay-invoice-${inv.id}`}>
                          <CreditCard className="mr-1 h-3.5 w-3.5" /> Pay Now
                        </Button>
                      ) : <span className="text-xs text-success">Paid</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Collect payment</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border p-4">
                <p className="text-sm text-muted-foreground">{payFor.invoice_number} · {payFor.customer_name}</p>
                <p className="mt-1 font-heading text-2xl font-bold">{money(payFor.total - (payFor.amount_paid || 0))}</p>
                <p className="text-xs text-muted-foreground">amount due</p>
              </div>
              <p className="text-xs text-muted-foreground">You'll be redirected to Stripe's secure checkout. On success the invoice is marked paid and a receipt is generated automatically via webhook.</p>
              <DialogFooter>
                <Button className="w-full rounded-full" onClick={() => payMut.mutate(payFor.id)} disabled={payMut.isPending} data-testid="confirm-pay-btn">
                  {payMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="mr-1.5 h-4 w-4" /> Pay {money(payFor.total - (payFor.amount_paid || 0))} with Stripe</>}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Email history · {historyFor?.invoice_number}</DialogTitle>
            <DialogDescription>Invoice and reminder emails sent for this invoice, newest first.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto" data-testid="email-history-list">
            {logsLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
            ) : emailLogs.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 text-center">
                <Mail className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No emails sent for this invoice yet.</p>
                {historyFor && (
                  <Button size="sm" className="mt-1 rounded-full" onClick={() => emailMut.mutate(historyFor.id)} disabled={emailMut.isPending} data-testid="history-send-now-btn">
                    <Mail className="mr-1.5 h-3.5 w-3.5" /> Send now
                  </Button>
                )}
              </div>
            ) : (
              (() => {
                const groups = [];
                const idx = {};
                for (const l of emailLogs) {
                  const key = `${l.kind}|${l.to}|${l.ok}`;
                  if (idx[key] === undefined) { idx[key] = groups.length; groups.push({ ...l, count: 1, last: l.created_at }); }
                  else { groups[idx[key]].count += 1; }
                }
                return groups.map((g) => (
                  <div key={g.id} className="flex items-start gap-2 rounded-lg border border-border p-3" data-testid={`email-log-${g.id}`}>
                    {g.ok ? <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-success" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-critical" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium capitalize">{g.kind}{g.count > 1 && <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground">sent {g.count}×</span>}</p>
                      <p className="truncate text-xs text-muted-foreground">to {g.to || "—"}</p>
                    </div>
                    <p className="shrink-0 text-xs text-muted-foreground">{new Date(g.last).toLocaleString("en-IN")}</p>
                  </div>
                ));
              })()
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
